import React, { useState, useEffect, useCallback } from 'react';
import { applicantStore } from '../ApplicantStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { dialogStore } from 'components/layout/modals/DialogStore';
import moment from 'moment';
import './ApplicantResumeHistory.sass';

export default function ApplicantResumeHistory({
	applicantId,
	handleSuccess,
	onDownload
}) {
	const [resumes, setResumes] = useState([]);
	const [loading, setLoading] = useState(true);

	const loadResumes = useCallback(async () => {
		setLoading(true);
		try {
			const result = await applicantStore.getResumes(applicantId);
			if (result.status === 'success') {
				setResumes(result.data);
			}
		} catch (error) {
			console.error('Error loading resumes:', error);
		} finally {
			setLoading(false);
		}
	}, [applicantId]);

	useEffect(() => {
		loadResumes();
	}, [applicantId, loadResumes]);

	const handleDownload = resume => {
		onDownload?.(resume);
	};

	const handleDelete = resume => {
		dialogStore.openDialog(
			{
				title: 'Delete Resume',
				message: 'This resume will be deleted. Are you sure?',
				confirmLabel: 'Delete',
				confirmButtonClass: 'is-danger'
			},
			async () => {
				try {
					const result = await applicantStore.deleteResume(
						applicantId,
						resume.id
					);
					if (result.status === 'success') {
						notificationStore.pushSuccessNotification(
							'Resume Deleted',
							'The resume has been successfully deleted.'
						);
						loadResumes();
						handleSuccess?.();
					} else {
						notificationStore.pushErrorNotification(
							'Delete Failed',
							'Failed to delete the resume.'
						);
					}
				} catch (error) {
					notificationStore.pushErrorNotification(
						'Delete Failed',
						'An error occurred while deleting the resume.'
					);
				}
			}
		);
	};

	const formatDate = date => {
		return moment(date).format('MMMM YYYY');
	};

	const truncateFileName = (name, maxLength = 30) => {
		if (!name || name.length <= maxLength) return name;
		const extension = name.split('.').pop();
		const baseName = name.substring(0, name.lastIndexOf('.'));
		const truncatedBase = baseName.substring(
			0,
			maxLength - extension.length - 4
		);
		return `${truncatedBase}...`;
	};

	return (
		<>
			<section className="modal-card-body has-text-centered">
				<div className="ApplicantResumeHistory">
					{loading ? (
						<div className="loading-state">
							<span>Loading...</span>
						</div>
					) : resumes.length === 0 ? (
						<div className="empty-state">
							<span>No resumes uploaded yet.</span>
						</div>
					) : (
						<div className="resume-list">
							{resumes.map(resume => (
								<div key={resume.id} className="resume-item">
									<div className="resume-icon">
										<img src="/ats/icons/notes-icon.svg" alt="Resume" />
									</div>
									<div className="resume-info">
										<span className="resume-date">
											{formatDate(resume.createdAt)}
										</span>
										<span className="resume-filename">
											{truncateFileName(resume.fileName)}
										</span>
									</div>
									<div className="resume-actions">
										<button
											type="button"
											className="action-btn delete-btn"
											onClick={() => handleDelete(resume)}
											title="Delete">
											<img src="/ats/icons/trash-icon.svg" alt="Delete" />
										</button>
										<button
											type="button"
											className="action-btn download-btn"
											onClick={() => handleDownload(resume)}
											title="Download">
											<img src="/ats/icons/download-icon.svg" alt="Download" />
										</button>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</section>
			<footer className="modal-card-foot"></footer>
		</>
	);
}
